'use client';

import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslation } from '@/hooks/useTranslation';
import { useRTL } from '@/hooks/useRTL';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
    CheckCircle, 
    Star, 
    Sparkles, 
    Zap, 
    Crown, 
    Shield,
    Clock,
    Award,
    Phone
} from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

export default function PricingPackagesSection() {
    const { t, isLoading } = useTranslation('teeth-whitening');
    const { isRTL } = useRTL();
    const [hoveredPackage, setHoveredPackage] = useState<string | null>(null);
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLDivElement>(null);
    const packagesRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isLoading) return;

        const ctx = gsap.context(() => {
            // Title animation
            if (titleRef.current) {
                gsap.fromTo(titleRef.current,
                    { opacity: 0, y: 50 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 1,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: titleRef.current,
                            start: "top 85%",
                            toggleActions: "play none none reverse"
                        }
                    }
                );
            }

            // Packages animation
            if (packagesRef.current?.children) {
                const packages = Array.from(packagesRef.current.children);
                gsap.fromTo(packages,
                    { opacity: 0, y: 30, scale: 0.95 },
                    {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        duration: 0.6,
                        stagger: 0.2,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: packagesRef.current,
                            start: "top 85%",
                            toggleActions: "play none none reverse"
                        }
                    }
                );
            }

        }, sectionRef);

        return () => ctx.revert();
    }, [isLoading]);

    const packages = [
        {
            id: 'basic',
            name: t('pricing.packages.basic.name'),
            subtitle: t('pricing.packages.basic.subtitle'),
            price: t('pricing.packages.basic.price'),
            originalPrice: t('pricing.packages.basic.originalPrice'),
            discount: t('pricing.packages.basic.discount'),
            icon: Sparkles,
            popular: false,
            features: [
                t('pricing.packages.basic.features.0'),
                t('pricing.packages.basic.features.1'),
                t('pricing.packages.basic.features.2'),
                t('pricing.packages.basic.features.3'),
                t('pricing.packages.basic.features.4')
            ],
            duration: t('pricing.packages.basic.duration'),
            guarantee: t('pricing.packages.basic.guarantee')
        },
        {
            id: 'premium',
            name: t('pricing.packages.premium.name'),
            subtitle: t('pricing.packages.premium.subtitle'),
            price: t('pricing.packages.premium.price'),
            originalPrice: t('pricing.packages.premium.originalPrice'),
            discount: t('pricing.packages.premium.discount'),
            icon: Crown,
            popular: true,
            features: [
                t('pricing.packages.premium.features.0'),
                t('pricing.packages.premium.features.1'),
                t('pricing.packages.premium.features.2'),
                t('pricing.packages.premium.features.3'),
                t('pricing.packages.premium.features.4'),
                t('pricing.packages.premium.features.5'),
                t('pricing.packages.premium.features.6')
            ],
            duration: t('pricing.packages.premium.duration'),
            guarantee: t('pricing.packages.premium.guarantee')
        },
        {
            id: 'deluxe',
            name: t('pricing.packages.deluxe.name'),
            subtitle: t('pricing.packages.deluxe.subtitle'),
            price: t('pricing.packages.deluxe.price'),
            originalPrice: t('pricing.packages.deluxe.originalPrice'),
            discount: t('pricing.packages.deluxe.discount'),
            icon: Zap,
            popular: false,
            features: [
                t('pricing.packages.deluxe.features.0'),
                t('pricing.packages.deluxe.features.1'),
                t('pricing.packages.deluxe.features.2'),
                t('pricing.packages.deluxe.features.3'),
                t('pricing.packages.deluxe.features.4'),
                t('pricing.packages.deluxe.features.5'),
                t('pricing.packages.deluxe.features.6'),
                t('pricing.packages.deluxe.features.7')
            ],
            duration: t('pricing.packages.deluxe.duration'),
            guarantee: t('pricing.packages.deluxe.guarantee')
        }
    ];

    if (isLoading) {
        return (
            <section className="py-16 lg:py-24 bg-white dark:bg-slate-800">
                <div className="container">
                    <div className="text-center mb-16 animate-pulse">
                        <div className="h-12 bg-slate-200 dark:bg-slate-700 rounded mb-6 max-w-4xl mx-auto"></div>
                        <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded max-w-3xl mx-auto"></div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="h-96 bg-slate-200 dark:bg-slate-700 rounded-xl animate-pulse"></div>
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section id="pricing" ref={sectionRef} className="py-16 lg:py-24 bg-white dark:bg-slate-800">
            <div className="container">
                <div ref={titleRef} className="text-center mb-16">
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-navy dark:text-white font-serif leading-tight mb-6">
                        {t('pricing.title.part1')} <span className="text-gold">{t('pricing.title.part2')}</span>
                    </h2>
                    <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
                        {t('pricing.subtitle')}
                    </p>
                </div>

                <div ref={packagesRef} className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                    {packages.map((pkg, index) => (
                        <Card 
                            key={pkg.id}
                            className={`relative border-2 transition-all duration-300 transform hover:scale-105 ${
                                pkg.popular 
                                    ? 'border-gold shadow-xl shadow-gold/20' 
                                    : 'border-slate-200 dark:border-slate-700 hover:border-gold/50'
                            } ${
                                hoveredPackage === pkg.id ? 'shadow-2xl' : 'shadow-lg'
                            } bg-white dark:bg-slate-800`}
                            onMouseEnter={() => setHoveredPackage(pkg.id)}
                            onMouseLeave={() => setHoveredPackage(null)}
                        >
                            {pkg.popular && (
                                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                    <div className="bg-gold text-navy px-4 py-2 rounded-full text-sm font-bold flex items-center gap-1">
                                        <Star className="w-4 h-4 fill-current" />
                                        {t('pricing.popular')}
                                    </div>
                                </div>
                            )}

                            <CardContent className="p-8">
                                {/* Package Header */}
                                <div className="text-center mb-8">
                                    <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${
                                        pkg.popular 
                                            ? 'bg-gradient-to-br from-gold to-gold-600' 
                                            : 'bg-gold/20'
                                    }`}>
                                        <pkg.icon className={`w-10 h-10 ${
                                            pkg.popular ? 'text-white' : 'text-gold'
                                        }`} />
                                    </div>
                                    
                                    <h3 className="text-2xl font-bold text-navy dark:text-white mb-2">
                                        {pkg.name}
                                    </h3>
                                    
                                    <p className="text-slate-600 dark:text-slate-300 mb-4">
                                        {pkg.subtitle}
                                    </p>

                                    {/* Pricing */}
                                    <div className="mb-6">
                                        <div className="flex items-center justify-center gap-2 mb-2">
                                            <span className="text-3xl font-bold text-gold">
                                                {pkg.price}
                                            </span>
                                            {pkg.originalPrice && (
                                                <span className="text-lg text-slate-600 dark:text-slate-300 line-through">
                                                    {pkg.originalPrice}
                                                </span>
                                            )}
                                        </div>
                                        {pkg.discount && (
                                            <div className="inline-block bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-3 py-1 rounded-full text-sm font-semibold">
                                                {pkg.discount}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Features */}
                                <div className="mb-8">
                                    <ul className="space-y-3">
                                        {pkg.features.map((feature, featureIndex) => (
                                            <li key={featureIndex} className="flex items-start gap-3">
                                                <CheckCircle className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
                                                <span className="text-slate-600 dark:text-slate-300 text-sm">
                                                    {feature}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {/* Package Details */}
                                <div className="mb-8 space-y-3">
                                    <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                                        <Clock className="w-4 h-4 text-gold" />
                                        <span className="text-sm">{pkg.duration}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                                        <Shield className="w-4 h-4 text-gold" />
                                        <span className="text-sm">{pkg.guarantee}</span>
                                    </div>
                                </div>

                                {/* CTA Button */}
                                <Button 
                                    className={`w-full py-3 rounded-xl font-semibold transition-all duration-300 ${
                                        pkg.popular
                                            ? 'bg-gold hover:bg-gold-600 text-navy shadow-lg hover:shadow-xl'
                                            : 'bg-white dark:bg-slate-700 border-2 border-gold text-gold hover:bg-gold hover:text-navy'
                                    }`}
                                    onClick={() => {
                                        const message = t('pricing.whatsappMessage', { package: pkg.id });
                                        window.open(`https://wa.me/905070808888?text=${encodeURIComponent(message)}`, '_blank');
                                    }}
                                >
                                    {t('pricing.cta')}
                                </Button>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                {/* Contact Info */}
                <div className="max-w-4xl mx-auto mt-16">
                    <Card className="bg-gold/10 border-gold/30">
                        <CardContent className="p-8 text-center">
                            <div className={`flex items-center justify-center gap-3 mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                <Phone className="w-8 h-8 text-gold" />
                                <h3 className="text-2xl font-bold text-navy dark:text-white">
                                    {t('pricing.contact.title')}
                                </h3>
                            </div>
                            <p className={`text-slate-600 dark:text-slate-300 mb-6 leading-relaxed ${isRTL ? 'text-right' : 'text-center'}`}>
                                {t('pricing.contact.description')}
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                                <Button 
                                    className="bg-gold hover:bg-gold-600 text-navy font-semibold px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                                >
                                    {t('pricing.contact.call')}
                                </Button>
                                <Button 
                                    variant="outline"
                                    className="border-gold text-gold hover:bg-gold hover:text-navy font-semibold px-6 py-3 rounded-xl"
                                >
                                    {t('pricing.contact.whatsapp')}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </section>
    );
}